/**
 * “模版库1 / 素材库1”查询数据源。
 *
 * 新页面只替换查询接口，增删改、上传、预览等交互继续复用原业务能力。
 * 查询结果放在独立集合中，避免覆盖原模版库、素材库的数据。
 */

import { materialService, templateService } from '@/services/artApi';
import {
  isSpawnedTemplateCopyName,
  mapMaterialDtoToPlatform,
  mapMaterialFolderDtoToPlatform,
  mapTemplateDtoToPlatform,
  mapTemplateFolderDtoToPlatform,
  normalizeMaterialFolderId,
  normalizeTemplateFolderId,
  unwrapApiList,
  unwrapApiResult,
} from '@/utils/artMapper';
import {
  getActiveDepartmentIdParam,
  resolvePlatformStorageKey,
  resolveSyncStoreIds,
} from '@/utils/materialApiContext';

function ensureQueryState(platform) {
  if (!Array.isArray(platform.queryTemplates)) platform.queryTemplates = [];
  if (!platform.queryTemplateFolders || typeof platform.queryTemplateFolders !== 'object') {
    platform.queryTemplateFolders = {};
  }
  if (!platform.queryTemplateTotalCount || typeof platform.queryTemplateTotalCount !== 'object') {
    platform.queryTemplateTotalCount = {};
  }
  if (!Array.isArray(platform.queryMaterials)) platform.queryMaterials = [];
  if (!platform.queryMaterialFolders || typeof platform.queryMaterialFolders !== 'object') {
    platform.queryMaterialFolders = {};
  }
  if (!platform.queryMaterialTotalCount || typeof platform.queryMaterialTotalCount !== 'object') {
    platform.queryMaterialTotalCount = {};
  }
}

function queryStoreId(storeId, user, stores = []) {
  return resolvePlatformStorageKey(storeId, user, stores);
}

function mergePendingFolders(remoteFolders, previousFolders = []) {
  const remoteIds = new Set(remoteFolders.map(folder => String(folder.id)));
  const remoteNames = new Set(remoteFolders.map(folder => folder.name));
  const pending = previousFolders.filter(folder => (
    folder?.pendingQuerySync
    && !remoteIds.has(String(folder.id))
    && !remoteNames.has(folder.name)
  ));
  return [...remoteFolders, ...pending];
}

/** 解析 queryTemplate / queryMaterial 分页包装：result.list + totalCount */
export function unwrapQueryLibraryRows(payload, totalCountKey) {
  const result = unwrapApiResult(payload);
  if (Array.isArray(result)) {
    return { rows: result, totalCount: null };
  }
  if (result && typeof result === 'object') {
    const rows = Array.isArray(result.list) ? result.list
      : Array.isArray(result.records) ? result.records
        : Array.isArray(result.data) ? result.data
          : unwrapApiList(payload);
    const rawTotal = result[totalCountKey] ?? result.totalCount ?? result.total;
    const totalCount = rawTotal != null && Number.isFinite(Number(rawTotal))
      ? Number(rawTotal)
      : null;
    return { rows, totalCount };
  }
  return { rows: [], totalCount: null };
}

function dedupeById(items = []) {
  const seen = new Set();
  return items.filter((item) => {
    const id = String(item?.id || '');
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

function isQueryTemplateFolderRow(row, folderType, folderId) {
  if (folderType === 1) return true;
  if (folderType === 2) return false;
  if (row.templateName || row.templateId) return false;
  return Boolean(row.folderName) && folderId != null && folderId !== '';
}

/** 解析 queryTemplate 返回：文件夹 + templateInfo 嵌套模版 */
export function parseQueryTemplateResult(payload, querySid = 's1') {
  const { rows, totalCount: apiTotalCount } = unwrapQueryLibraryRows(payload, 'templateTotalCount');
  const folders = [];
  const templates = [];

  rows.forEach((row) => {
    if (!row || typeof row !== 'object') return;

    const infoList = Array.isArray(row.templateInfo) ? row.templateInfo : [];
    const folderType = Number(row.templateFolderType);
    const folderId = row.id ?? row.folderId;
    const isFolderRow = isQueryTemplateFolderRow(row, folderType, folderId);

    if (isFolderRow) {
      const folderName = (row.folderName != null && String(row.folderName).trim() !== '')
        ? String(row.folderName).trim()
        : (row.name != null && String(row.name).trim() !== '' ? String(row.name).trim() : '文件夹');
      const folder = mapTemplateFolderDtoToPlatform({
        ...row,
        name: folderName,
        folderName,
        id: folderId,
        templateCount: row.templateCount ?? infoList.length,
      });
      if (folder.id) folders.push(folder);
      infoList.forEach((item) => {
        if (!item?.id && !item?.templateId) return;
        const itemName = (item.folderName != null && String(item.folderName).trim() !== '')
          ? String(item.folderName).trim()
          : ((item.name != null && String(item.name).trim() !== '')
            ? String(item.name).trim()
            : (item.templateName || '未命名模版'));
        templates.push(mapTemplateDtoToPlatform({
          ...item,
          name: itemName,
          folderId: item.folderId ?? folderId,
        }, querySid));
      });
      return;
    }

    if (infoList.length) {
      infoList.forEach((item) => {
        if (!item?.id && !item?.templateId) return;
        const resolvedName = (row.folderName != null && String(row.folderName).trim() !== '')
          ? String(row.folderName).trim()
          : ((row.name != null && String(row.name).trim() !== '')
            ? String(row.name).trim()
            : ((item.folderName != null && String(item.folderName).trim() !== '')
              ? String(item.folderName).trim()
              : ((item.name != null && String(item.name).trim() !== '')
                ? String(item.name).trim()
                : (item.templateName || row.templateName || '未命名模版'))));
        templates.push(mapTemplateDtoToPlatform({
          ...row,
          ...item,
          name: resolvedName,
          folderName: row.folderName || item.folderName,
          folderId: normalizeTemplateFolderId(item.folderId ?? row.folderId ?? 0),
        }, querySid));
      });
      return;
    }

    if (row.templateName || row.name || row.folderName || row.templateId || folderType === 2) {
      const rowName = (row.folderName != null && String(row.folderName).trim() !== '')
        ? String(row.folderName).trim()
        : (row.name != null && String(row.name).trim() !== '' ? String(row.name).trim() : (row.templateName || '未命名模版'));
      templates.push(mapTemplateDtoToPlatform({
        ...row,
        name: rowName,
        folderId: normalizeTemplateFolderId(row.folderId ?? 0),
      }, querySid));
    }
  });

  const allFolders = dedupeById(folders);
  const allTemplates = dedupeById(templates);
  const visibleFolders = allFolders.filter(item => !isSpawnedTemplateCopyName(item.name));
  const visibleTemplates = allTemplates.filter(item => !isSpawnedTemplateCopyName(item.name));
  const hiddenCopies = allTemplates.length - visibleTemplates.length;
  const totalCount = apiTotalCount != null
    ? Math.max(0, apiTotalCount - hiddenCopies)
    : visibleTemplates.length;
  return { folders: visibleFolders, templates: visibleTemplates, totalCount };
}

/** 解析 queryMaterial 返回：文件夹 + materialInfo 嵌套素材 */
export function parseQueryMaterialResult(payload, querySid = 's1') {
  const { rows, totalCount: apiTotalCount } = unwrapQueryLibraryRows(payload, 'materialTotalCount');
  const folders = [];
  const materials = [];

  rows.forEach((row) => {
    if (!row || typeof row !== 'object') return;

    const infoList = Array.isArray(row.materialInfo) ? row.materialInfo : [];
    const folderType = Number(row.materialFolderType);
    const folderId = row.id ?? row.folderId;
    const isFolderRow = folderType === 1
      || (row.folderName && folderId != null && folderId !== '');

    if (isFolderRow) {
      const folderName = (row.folderName != null && String(row.folderName).trim() !== '')
        ? String(row.folderName).trim()
        : (row.name != null && String(row.name).trim() !== '' ? String(row.name).trim() : '文件夹');
      const folder = mapMaterialFolderDtoToPlatform({
        ...row,
        name: folderName,
        folderName,
        id: folderId,
        materialCount: row.materialCount ?? infoList.length,
      });
      if (folder.id) folders.push(folder);
      infoList.forEach((item) => {
        if (!item?.id && !item?.materialId) return;
        const itemName = (item.folderName != null && String(item.folderName).trim() !== '')
          ? String(item.folderName).trim()
          : ((item.name != null && String(item.name).trim() !== '')
            ? String(item.name).trim()
            : (item.materialName || '未命名素材'));
        materials.push(mapMaterialDtoToPlatform({
          ...item,
          name: itemName,
          folderId: item.folderId ?? folderId,
        }, querySid));
      });
      return;
    }

    if (infoList.length) {
      infoList.forEach((item) => {
        if (!item?.id && !item?.materialId) return;
        const resolvedName = (row.folderName != null && String(row.folderName).trim() !== '')
          ? String(row.folderName).trim()
          : ((row.name != null && String(row.name).trim() !== '')
            ? String(row.name).trim()
            : ((item.folderName != null && String(item.folderName).trim() !== '')
              ? String(item.folderName).trim()
              : ((item.name != null && String(item.name).trim() !== '')
                ? String(item.name).trim()
                : (item.materialName || row.materialName || '未命名素材'))));
        materials.push(mapMaterialDtoToPlatform({
          ...row,
          ...item,
          name: resolvedName,
          folderName: row.folderName || item.folderName,
          folderId: normalizeMaterialFolderId(item.folderId ?? row.folderId ?? 0),
        }, querySid));
      });
      return;
    }

    if (row.materialName || row.name || row.folderName || row.materialId || row.originUrl || folderType === 2) {
      const rowName = (row.folderName != null && String(row.folderName).trim() !== '')
        ? String(row.folderName).trim()
        : (row.name != null && String(row.name).trim() !== '' ? String(row.name).trim() : (row.materialName || '未命名素材'));
      materials.push(mapMaterialDtoToPlatform({
        ...row,
        name: rowName,
        folderId: normalizeMaterialFolderId(row.folderId ?? 0),
      }, querySid));
    }
  });

  const totalCount = apiTotalCount ?? materials.length;
  return { folders, materials, totalCount };
}

export async function syncQueryTemplateLibrary(platform, storeId, user, apiContext = {}) {
  ensureQueryState(platform);
  const context = { ...apiContext, storeId, user };
  const storeIds = resolveSyncStoreIds(context);
  await Promise.all(storeIds.map(async (sid) => {
    const params = getActiveDepartmentIdParam({ ...context, storeId: sid });
    const querySid = queryStoreId(sid, user, context.stores || []);
    const templatePayload = await templateService.queryTemplate(params);
    const { folders, templates, totalCount } = parseQueryTemplateResult(templatePayload, querySid);

    platform.queryTemplateFolders[querySid] = mergePendingFolders(
      folders,
      platform.queryTemplateFolders[querySid],
    );
    platform.queryTemplateTotalCount[querySid] = totalCount;
    if (querySid === 'all') {
      platform.queryTemplates = templates;
    } else {
      platform.queryTemplates = dedupeById(
        platform.queryTemplates
          .filter(item => item.storeId !== querySid)
          .concat(templates),
      );
    }
    return { folders, templates, totalCount };
  }));
}

export async function syncQueryMaterialLibrary(platform, storeId, user, apiContext = {}) {
  ensureQueryState(platform);
  const context = { ...apiContext, storeId, user };
  const storeIds = resolveSyncStoreIds(context);
  await Promise.all(storeIds.map(async (sid) => {
    const params = getActiveDepartmentIdParam({ ...context, storeId: sid });
    const querySid = queryStoreId(sid, user, context.stores || []);
    const materialPayload = await materialService.queryMaterial(params);
    const { folders, materials, totalCount } = parseQueryMaterialResult(materialPayload, querySid);

    platform.queryMaterialFolders[querySid] = mergePendingFolders(
      folders,
      platform.queryMaterialFolders[querySid],
    );
    platform.queryMaterialTotalCount[querySid] = totalCount;
    if (querySid === 'all') {
      platform.queryMaterials = materials;
    } else {
      platform.queryMaterials = platform.queryMaterials
        .filter(item => item.storeId !== querySid)
        .concat(materials);
    }
    return { folders, materials, totalCount };
  }));
}
